/**
 * Vertical timeline of a driver's stops, shown in Active Context.
 *
 * Each stop is one of three states — completed (marked done), current
 * (highlighted as the driver's present focus), or upcoming (muted) — rendered
 * chronologically so a dispatcher can see at a glance where the driver is in
 * their day. Mocked/demo data; no live wiring.
 */

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { cityStateCode } from "../codes";
import { accent, text, zinc } from "../colors";
import type { DriverStop, StopState } from "../types";
import { Chip, CodeBadge, FieldLabel } from "./primitives";

const STATE_META: Record<StopState, { color: string; label: string; icon: React.ReactNode }> = {
  completed: { color: accent.green, label: "Done", icon: <CheckCircleIcon sx={{ fontSize: 20 }} /> },
  current: { color: accent.purple, label: "Now", icon: <RadioButtonCheckedIcon sx={{ fontSize: 20 }} /> },
  upcoming: { color: zinc[600], label: "Upcoming", icon: <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} /> },
};

function StopRow({ stop, last }: { stop: DriverStop; last: boolean }) {
  const meta = STATE_META[stop.state];
  const current = stop.state === "current";
  const completed = stop.state === "completed";
  return (
    <Box sx={{ display: "flex", gap: 1.5, position: "relative" }}>
      {/* rail + marker */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <Box
          sx={{
            color: meta.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...(current && {
              animation: "stopPulse 1.8s ease-in-out infinite",
              "@keyframes stopPulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.45 },
              },
            }),
          }}
        >
          {meta.icon}
        </Box>
        {!last && (
          <Box
            sx={{
              flex: 1,
              width: "2px",
              minHeight: 24,
              my: 0.25,
              bgcolor: completed ? accent.greenBorder : zinc[700],
            }}
          />
        )}
      </Box>

      {/* body */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          pb: last ? 0 : 1.75,
          ...(current && {
            mt: -0.5,
            mb: last ? 0 : 1,
            p: 1.25,
            borderRadius: "10px",
            bgcolor: accent.purpleSoft,
            border: `1px solid ${accent.purpleBorder}`,
          }),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: stop.state === "upcoming" ? text.secondary : text.primary,
            }}
          >
            {stop.location}
          </Typography>
          <Chip
            label={meta.label}
            tone={stop.state === "completed" ? "green" : stop.state === "current" ? "purple" : "neutral"}
          />
        </Box>
        <Typography sx={{ fontSize: 12.5, color: text.muted, mt: 0.25 }}>
          {stop.city}
          <CodeBadge code={cityStateCode(stop.city)} />
          {stop.purpose ? ` · ${stop.purpose}` : ""}
        </Typography>
        {(stop.appointment || stop.eta || stop.milesToStop != null) && (
          <Box sx={{ display: "flex", gap: 2.5, mt: 0.75 }}>
            {stop.milesToStop != null && (
              <Box>
                <FieldLabel>Miles to go</FieldLabel>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: current ? accent.purple : text.primary }}>
                  ~{stop.milesToStop} mi
                </Typography>
              </Box>
            )}
            {stop.appointment && (
              <Box>
                <FieldLabel>Appointment</FieldLabel>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: text.primary }}>{stop.appointment}</Typography>
              </Box>
            )}
            {stop.eta && (
              <Box>
                <FieldLabel>{completed ? "Actual" : "ETA"}</FieldLabel>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: current ? accent.purple : text.primary }}>
                  {stop.eta}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function StopsTimeline({ stops }: { stops: DriverStop[] }) {
  if (stops.length === 0) {
    return <Typography sx={{ fontSize: 13, color: text.muted }}>No stops on this trip.</Typography>;
  }
  return (
    <Box>
      {stops.map((stop, i) => (
        <StopRow key={stop.id} stop={stop} last={i === stops.length - 1} />
      ))}
    </Box>
  );
}
